import React, { useState, useEffect, useCallback } from 'react';
import { 
  Database, Package, Users, Store, RefreshCw, 
  TrendingUp, BarChart3, PieChart, Layers,
  ChevronDown, ChevronUp, Search
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DatabaseStatsTab = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [categoryStats, setCategoryStats] = useState([]);
  const [expandedSeller, setExpandedSeller] = useState(null);
  const [sellerCategories, setSellerCategories] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch general stats
      const statsRes = await fetch(`${API_URL}/api/admin/data-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }

      // Fetch category stats
      const catRes = await fetch(`${API_URL}/api/admin/category-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (catRes.ok) {
        const catData = await catRes.json();
        setCategoryStats(catData.categories || []);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSellerCategories = async (sellerId, sellerName) => {
    if (sellerCategories[sellerId]) {
      setExpandedSeller(expandedSeller === sellerId ? null : sellerId);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_URL}/api/products?seller_name=${encodeURIComponent(sellerName)}&limit=1000`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (res.ok) {
        const data = await res.json();
        const products = data.products || [];
        
        // Group by category
        const catCounts = {};
        products.forEach(p => {
          const cat = p.category || 'Kategorisiz';
          catCounts[cat] = (catCounts[cat] || 0) + 1;
        });
        
        // Sort by count
        const sorted = Object.entries(catCounts)
          .sort((a, b) => b[1] - a[1])
          .map(([name, count]) => ({ name, count }));
        
        setSellerCategories(prev => ({ ...prev, [sellerId]: sorted }));
        setExpandedSeller(sellerId);
      }
    } catch (err) {
      console.error('Failed to fetch seller categories:', err);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const filteredSellers = stats?.products_per_seller?.filter(seller => 
    seller.seller_name?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const totalProducts = stats?.total_products || 0;
  const maxSellerProducts = Math.max(...(stats?.products_per_seller?.map(s => s.product_count) || [1]));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Database className="w-7 h-7 text-blue-600" />
            Veritabanı İstatistikleri
          </h2>
          <p className="text-gray-500 mt-1">Satıcı ve kategori bazlı ürün dağılımları</p>
        </div>
        <button
          onClick={fetchStats}
          className="flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Yenile
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Toplam Ürün</p>
              <p className="text-3xl font-bold mt-1">{totalProducts.toLocaleString()}</p>
            </div>
            <Package className="w-10 h-10 text-blue-200" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-100 text-sm">Aktif Satıcı</p>
              <p className="text-3xl font-bold mt-1">{stats?.products_per_seller?.length || 0}</p>
            </div>
            <Store className="w-10 h-10 text-emerald-200" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Kategori Sayısı</p>
              <p className="text-3xl font-bold mt-1">{categoryStats.length}</p>
            </div>
            <Layers className="w-10 h-10 text-purple-200" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm">Toplam Kullanıcı</p>
              <p className="text-3xl font-bold mt-1">{stats?.total_users?.toLocaleString() || 0}</p>
            </div>
            <Users className="w-10 h-10 text-orange-200" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sellers Panel */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-600" />
                Satıcı Başına Ürün Dağılımı
              </h3>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Satıcı ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
            {filteredSellers.map((seller, idx) => {
              const percentage = ((seller.product_count / totalProducts) * 100).toFixed(1);
              const barWidth = (seller.product_count / maxSellerProducts) * 100;
              const isExpanded = expandedSeller === seller.seller_id;
              const categories = sellerCategories[seller.seller_id] || [];

              return (
                <div key={seller.seller_id || idx} className="group">
                  <div 
                    className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => fetchSellerCategories(seller.seller_id, seller.seller_name)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{seller.seller_name}</span>
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                          %{percentage}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-700">
                          {seller.product_count?.toLocaleString()} ürün
                        </span>
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        )}
                      </div>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full transition-all duration-500"
                        style={{ width: `${barWidth}%` }}
                      />
                    </div>
                  </div>
                  
                  {/* Expanded Category Details */}
                  {isExpanded && categories.length > 0 && (
                    <div className="px-4 pb-4 bg-gray-50">
                      <div className="bg-white rounded-lg border border-gray-200 p-3">
                        <p className="text-xs font-medium text-gray-500 mb-2">Kategori Dağılımı</p>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {categories.slice(0, 10).map((cat, catIdx) => (
                            <div key={catIdx} className="flex items-center justify-between text-sm">
                              <span className="text-gray-700 truncate flex-1 mr-2">{cat.name}</span>
                              <span className="text-gray-500 font-medium whitespace-nowrap">
                                {cat.count.toLocaleString()}
                              </span>
                            </div>
                          ))}
                          {categories.length > 10 && (
                            <p className="text-xs text-gray-400 pt-1">
                              +{categories.length - 10} daha fazla kategori
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Categories Panel */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-100 bg-gray-50">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-purple-600" />
              Genel Kategori Dağılımı
            </h3>
            <p className="text-sm text-gray-500 mt-1">Tüm satıcıların toplam kategori istatistikleri</p>
          </div>
          
          <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
            {categoryStats.slice(0, 30).map((cat, idx) => {
              const percentage = ((cat.count / totalProducts) * 100).toFixed(1);
              const colors = [
                'from-blue-500 to-blue-400',
                'from-emerald-500 to-emerald-400',
                'from-purple-500 to-purple-400',
                'from-orange-500 to-orange-400',
                'from-pink-500 to-pink-400',
                'from-cyan-500 to-cyan-400',
                'from-amber-500 to-amber-400',
                'from-rose-500 to-rose-400',
              ];
              const colorClass = colors[idx % colors.length];

              return (
                <div key={idx} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900 truncate flex-1 mr-4">
                      {cat.category || 'Kategorisiz'}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                        %{percentage}
                      </span>
                      <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">
                        {cat.count?.toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className={`h-full bg-gradient-to-r ${colorClass} rounded-full`}
                      style={{ width: `${Math.min(parseFloat(percentage) * 2, 100)}%` }}
                    />
                  </div>
                </div>
              );
            })}
            
            {categoryStats.length > 30 && (
              <div className="p-4 text-center text-sm text-gray-500">
                +{categoryStats.length - 30} daha fazla kategori
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5 text-emerald-600" />
          Hızlı İstatistikler
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">En Çok Ürünü Olan Satıcı</p>
            <p className="font-semibold text-gray-900 mt-1">
              {stats?.products_per_seller?.[0]?.seller_name || '-'}
            </p>
            <p className="text-xs text-gray-500">
              {stats?.products_per_seller?.[0]?.product_count?.toLocaleString() || 0} ürün
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">En Popüler Kategori</p>
            <p className="font-semibold text-gray-900 mt-1 truncate">
              {categoryStats[0]?.category || '-'}
            </p>
            <p className="text-xs text-gray-500">
              {categoryStats[0]?.count?.toLocaleString() || 0} ürün
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">Ortalama Ürün/Satıcı</p>
            <p className="font-semibold text-gray-900 mt-1">
              {stats?.products_per_seller?.length > 0 
                ? Math.round(totalProducts / stats.products_per_seller.length).toLocaleString()
                : 0}
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">Toplam Sipariş</p>
            <p className="font-semibold text-gray-900 mt-1">
              {stats?.total_orders?.toLocaleString() || 0}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DatabaseStatsTab;
